import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import config from '../../utils/config'
import type { RootState } from '../../store'
import { useWeb3Context, setGameSessionWalletAddress } from '../../contexts/web3Context'
import { clearSessionExpiresAt } from '../../utils/sessionExpiration'
import { alert } from '../Alert/Alert'
import { EMAIL_RESEND_COOLDOWN_SEC } from './emailAuthConstants'

const apiBase = `${config.server || ''}${config.baseURL}`
const DISMISS_KEY = 'eclipsera_add_email_banner_dismissed'

function formatCooldown(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function AddEmailPromptBanner() {
  const { t } = useTranslation()
  const location = useLocation()
  const { address, connected, disconnect } = useWeb3Context()
  const needsEmailBinding = useSelector((s: RootState) => s.userModule.user.needsEmailBinding === true)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })
  const [modalOpen, setModalOpen] = useState(false)
  const [step, setStep] = useState<'form' | 'verify'>('form')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [codeSentHint, setCodeSentHint] = useState(false)
  const codeInputRef = useRef<HTMLInputElement>(null)

  const hideForRoute =
    location.pathname === '/start' ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname.startsWith('/error/')

  useEffect(() => {
    const id = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore */
    }
    setDismissed(true)
  }, [])

  const onSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      await axios.post(
        `${apiBase}/user/email/add-to-wallet-request`,
        { email, password },
        { withCredentials: true },
      )
      setCode('')
      setCodeSentHint(true)
      setStep('verify')
      setResendCooldown(EMAIL_RESEND_COOLDOWN_SEC)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('addEmail.errors.requestFailed')
      alert.open(msg)
    } finally {
      setBusy(false)
    }
  }

  const onResend = async () => {
    if (busy || resendCooldown > 0) return
    setBusy(true)
    try {
      const { data } = await axios.post(
        `${apiBase}/user/email/add-to-wallet-resend`,
        { email },
        { withCredentials: true },
      )
      const next = Number(data?.retryAfter || EMAIL_RESEND_COOLDOWN_SEC)
      setResendCooldown(next > 0 ? next : EMAIL_RESEND_COOLDOWN_SEC)
      setCodeSentHint(true)
    } catch (err: unknown) {
      const retryAfter = Number((err as { response?: { data?: { retryAfter?: number } } })?.response?.data?.retryAfter || 0)
      if (retryAfter > 0) setResendCooldown(retryAfter)
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('addEmail.errors.genericFailed')
      alert.open(msg)
    } finally {
      setBusy(false)
    }
  }

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      await axios.post(
        `${apiBase}/user/email/add-to-wallet-verify`,
        { email, code },
        { withCredentials: true },
      )
      setModalOpen(false)
      clearSessionExpiresAt()
      setGameSessionWalletAddress(null)
      try {
        await disconnect()
      } catch {
        /* ignore */
      }
      alert.open(t('addEmail.successBody'))
      window.location.assign(`${window.location.origin}/`)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('addEmail.errors.verificationFailed')
      alert.open(msg)
    } finally {
      setBusy(false)
    }
  }

  const openModal = () => {
    setStep('form')
    setCode('')
    setCodeSentHint(false)
    setModalOpen(true)
  }

  useEffect(() => {
    if (!modalOpen || step !== 'verify') return
    let cancelled = false
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return
        const el = codeInputRef.current
        if (el) {
          el.focus()
          try {
            el.select()
          } catch {
            /* ignore */
          }
        }
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [modalOpen, step])

  const closeModal = () => {
    if (!busy) setModalOpen(false)
  }

  if (!connected || !address || !needsEmailBinding || dismissed || hideForRoute) {
    return null
  }

  const banner = (
    <div
      className="pointer-events-auto fixed left-0 right-0 top-0 z-[10050] flex flex-wrap items-center justify-center gap-2 border-b border-amber-500/40 bg-amber-950/95 px-3 py-2 text-center text-sm text-amber-100 shadow-lg"
      role="region"
      aria-label={t('addEmail.modalTitle')}
    >
      <span className="max-w-3xl">{t('addEmail.bannerText')}</span>
      <button
        type="button"
        className="rounded-md bg-amber-500 px-3 py-1.5 font-semibold text-black hover:bg-amber-400"
        onClick={openModal}
      >
        {t('addEmail.bannerAdd')}
      </button>
      <button type="button" className="text-amber-200/90 underline hover:text-white" onClick={dismiss}>
        {t('addEmail.bannerLater')}
      </button>
    </div>
  )

  const modal =
    modalOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/70 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-email-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="relative z-[20001] w-full max-w-md rounded-xl border border-amber-600/40 bg-gray-950 p-6 shadow-2xl"
          style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="absolute right-3 top-3 text-gray-400 hover:text-white"
            onClick={closeModal}
            aria-label={t('common.close')}
          >
            ×
          </button>
          <h2 id="add-email-modal-title" className="mb-2 text-lg font-semibold text-white">
            {t('addEmail.modalTitle')}
          </h2>
          <p className="mb-4 text-xs text-gray-400">{t('addEmail.modalHint')}</p>

          {step === 'form' ? (
            <form onSubmit={onSendCode} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-gray-400">{t('addEmail.emailLabel')}</label>
                <input
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-400">{t('addEmail.passwordLabel')}</label>
                <input
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-amber-600 py-2.5 text-sm font-medium text-black hover:bg-amber-500 disabled:opacity-50"
              >
                {busy ? t('emailAuth.panel.busyEllipsis') : t('addEmail.sendCode')}
              </button>
            </form>
          ) : (
            <form onSubmit={onVerify} className="space-y-3">
              {codeSentHint && (
                <p className="rounded-lg border border-emerald-700/50 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-200">
                  {t('addEmail.codeSent')}
                </p>
              )}
              <div>
                <label className="mb-1 block text-xs text-gray-400">{t('addEmail.emailLabel')}</label>
                <div className="rounded-lg border border-gray-600 bg-gray-900/80 px-3 py-2 text-sm text-gray-300">
                  {email}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-400" htmlFor="add-email-verify-code">
                  {t('addEmail.codeLabel')}
                </label>
                <input
                  id="add-email-verify-code"
                  ref={codeInputRef}
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white"
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' } as React.CSSProperties}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  name="add-email-otp"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  placeholder="000000"
                  spellCheck={false}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-amber-600 py-2.5 text-sm font-medium text-black hover:bg-amber-500 disabled:opacity-50"
              >
                {busy ? t('emailAuth.panel.busyEllipsis') : t('addEmail.verify')}
              </button>
              <button
                type="button"
                disabled={busy || resendCooldown > 0}
                className="w-full rounded-lg bg-gray-800 py-2 text-sm hover:bg-gray-700 disabled:opacity-50"
                onClick={onResend}
              >
                {resendCooldown > 0
                  ? t('addEmail.resendWait', { time: formatCooldown(resendCooldown) })
                  : t('addEmail.resend')}
              </button>
              <button
                type="button"
                className="w-full text-sm text-gray-400 hover:text-white"
                onClick={() => setStep('form')}
              >
                {t('common.back')}
              </button>
            </form>
          )}
        </div>
      </div>,
      document.body,
    )

  return (
    <>
      {banner}
      {modal}
    </>
  )
}
